import React, { useCallback, useState, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { TextField, Autocomplete } from '@mui/material';
import api from 'utils/api';

const CategorySelector = ({ control, size = 'medium' }) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [loading, setLoading] = useState(false);
  const searchRequest = useRef(0);

  const searchCategories = async (val) => {
    const request = ++searchRequest.current;
    const trimmed = val?.trim();
    setSearchError('');
    setOptions([]);
    setLoading(Boolean(trimmed));
    if (!trimmed) return;
    try {
      const res = await api.get(`/category?name=${encodeURIComponent(trimmed)}`);
      if (!Array.isArray(res?.content)) throw new Error('Invalid category suggestions');
      if (request === searchRequest.current) setOptions(res.content);
    } catch (error) {
      if (request === searchRequest.current && !error.sessionHandled) {
        setSearchError('Categories could not be loaded. Edit your search and try again.');
      }
    } finally {
      if (request === searchRequest.current) setLoading(false);
    }
  };

  const getLabel = useCallback((o) => o?.name ?? '', []);

  return (
    <Controller
      name="categories"
      control={control}
      render={({ field: { onChange, value, onBlur, ref }, fieldState: { error } }) => {
        const val =
          Array.isArray(value) ? value :
          value && typeof value === 'object' ? [value] :
          value ? options.filter((o) => o.categoryId === value) : [];

        return (
          <Autocomplete
            multiple
            loading={loading}
            onBlur={onBlur}
            size={size}
            options={options}
            value={val}
            inputValue={inputValue}
            getOptionLabel={getLabel}
            open={open}
            onOpen={() => {
              if (inputValue.trim().length > 0) setOpen(true);
            }}
            onClose={() => setOpen(false)}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
                if (newInputValue.trim().length > 0) {
                  setOpen(true);
                  searchCategories(newInputValue);
                } else {
                  setOpen(false);
                  searchCategories('');
                }
              }
            }}
            onChange={(e, val) => {
              onChange(val);
              setInputValue('');
              searchCategories('');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                inputRef={ref}
                error={Boolean(error || searchError)}
                helperText={error?.message || searchError || 'Search for the categories that best represent your business.'}
                label="Categories"
                variant="outlined"
              />
            )}
            ChipProps={{ color: 'primary' }}
          />
        );
      }}
    />
  );
};

export default CategorySelector;
